import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "@/lib/api";
import { Calculator, FileBadge2, Loader2, Percent, Save, ShieldCheck, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const EMPTY_PROFILE = {
  name: "",
  scope: "global",
  product_id: "",
  category_slug: "",
  hs_prefix: "",
  origin_country: "",
  duty_rate: 0,
  regulatory_duty_rate: 0,
  supplementary_duty_rate: 0,
  vat_rate: 0,
  advance_tax_rate: 0,
  advance_income_tax_rate: 0,
  exemption_rate: 0,
  rebate_rate: 0,
  notes: "",
  legal_tips: "",
  active: true,
};

const EMPTY_CALC = {
  product_id: "",
  product_name: "",
  hs_code: "",
  category_slug: "",
  origin_country: "",
  currency: "USD",
  invoice_value: 0,
  freight_cost: 0,
  insurance_cost: 0,
  other_cost: 0,
  use_profile_defaults: true,
  duty_rate: "",
  regulatory_duty_rate: "",
  supplementary_duty_rate: "",
  vat_rate: "",
  advance_tax_rate: "",
  advance_income_tax_rate: "",
  exemption_rate: "",
  rebate_rate: "",
};

function rateInput(value, onChange) {
  return (
    <Input
      type="number"
      step="0.01"
      min="0"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="mt-1.5"
    />
  );
}

export default function AdminTaxTools() {
  const qc = useQueryClient();
  const [profileForm, setProfileForm] = useState(EMPTY_PROFILE);
  const [editingProfileId, setEditingProfileId] = useState("");
  const [calcForm, setCalcForm] = useState(EMPTY_CALC);
  const [result, setResult] = useState(null);

  const { data: products = [] } = useQuery({
    queryKey: ["admin-products"],
    queryFn: async () => (await api.get("/admin/products")).data,
  });

  const { data: profiles = [], isLoading } = useQuery({
    queryKey: ["admin-tax-profiles"],
    queryFn: async () => (await api.get("/admin/tax/profiles")).data,
  });

  const selectedProduct = useMemo(
    () => products.find((item) => item.id === calcForm.product_id) || null,
    [products, calcForm.product_id]
  );

  useEffect(() => {
    if (!selectedProduct) return;
    setCalcForm((prev) => ({
      ...prev,
      product_name: selectedProduct.name || "",
      hs_code: selectedProduct.hs_code || "",
      category_slug: selectedProduct.category_slug || "",
      origin_country: selectedProduct.origin_country || "",
    }));
  }, [selectedProduct]);

  const saveProfileMut = useMutation({
    mutationFn: async (payload) => {
      const body = {
        ...payload,
        duty_rate: Number(payload.duty_rate || 0),
        regulatory_duty_rate: Number(payload.regulatory_duty_rate || 0),
        supplementary_duty_rate: Number(payload.supplementary_duty_rate || 0),
        vat_rate: Number(payload.vat_rate || 0),
        advance_tax_rate: Number(payload.advance_tax_rate || 0),
        advance_income_tax_rate: Number(payload.advance_income_tax_rate || 0),
        exemption_rate: Number(payload.exemption_rate || 0),
        rebate_rate: Number(payload.rebate_rate || 0),
        legal_tips: String(payload.legal_tips || "")
          .split("\n")
          .map((line) => line.trim())
          .filter(Boolean),
      };
      if (editingProfileId) {
        return (await api.put(`/admin/tax/profiles/${editingProfileId}`, body)).data;
      }
      return (await api.post("/admin/tax/profiles", body)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-tax-profiles"] });
      toast.success(editingProfileId ? "Tax profile updated" : "Tax profile created");
      setEditingProfileId("");
      setProfileForm(EMPTY_PROFILE);
    },
    onError: (e) => toast.error(formatApiError(e)),
  });

  const deleteProfileMut = useMutation({
    mutationFn: async (id) => api.delete(`/admin/tax/profiles/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-tax-profiles"] });
      toast.success("Tax profile deleted");
      if (editingProfileId) {
        setEditingProfileId("");
        setProfileForm(EMPTY_PROFILE);
      }
    },
    onError: (e) => toast.error(formatApiError(e)),
  });

  const calcMut = useMutation({
    mutationFn: async (payload) => {
      const body = {
        ...payload,
        invoice_value: Number(payload.invoice_value || 0),
        freight_cost: Number(payload.freight_cost || 0),
        insurance_cost: Number(payload.insurance_cost || 0),
        other_cost: Number(payload.other_cost || 0),
        duty_rate: payload.duty_rate === "" ? null : Number(payload.duty_rate),
        regulatory_duty_rate: payload.regulatory_duty_rate === "" ? null : Number(payload.regulatory_duty_rate),
        supplementary_duty_rate: payload.supplementary_duty_rate === "" ? null : Number(payload.supplementary_duty_rate),
        vat_rate: payload.vat_rate === "" ? null : Number(payload.vat_rate),
        advance_tax_rate: payload.advance_tax_rate === "" ? null : Number(payload.advance_tax_rate),
        advance_income_tax_rate: payload.advance_income_tax_rate === "" ? null : Number(payload.advance_income_tax_rate),
        exemption_rate: payload.exemption_rate === "" ? null : Number(payload.exemption_rate),
        rebate_rate: payload.rebate_rate === "" ? null : Number(payload.rebate_rate),
      };
      return (await api.post("/admin/tax/calculate", body)).data;
    },
    onSuccess: (data) => setResult(data),
    onError: (e) => toast.error(formatApiError(e)),
  });

  const startEdit = (profile) => {
    setEditingProfileId(profile.id);
    setProfileForm({
      ...profile,
      legal_tips: Array.isArray(profile.legal_tips) ? profile.legal_tips.join("\n") : "",
    });
  };

  const matchedProfile = useMemo(
    () => profiles.find((item) => item.id === result?.matched_profile_id) || null,
    [profiles, result]
  );

  return (
    <div data-testid="admin-tax-tools">
      <div className="flex items-center justify-between gap-4 flex-wrap mb-8">
        <div>
          <span className="badge-purple">
            <Calculator className="h-3.5 w-3.5" /> Admin Finance Desk
          </span>
          <h1 className="font-display text-4xl font-black tracking-tighter mt-3 text-[var(--rsi-navy)]">
            Tax Calculator
          </h1>
          <p className="text-gray-600 text-sm mt-1">
            Calculates duty, VAT, advance taxes, lawful exemptions, and potential savings for admin review only.
          </p>
        </div>
      </div>

      <div className="grid xl:grid-cols-12 gap-6">
        <div className="xl:col-span-7 space-y-6">
          <section className="rsi-card p-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="h-10 w-10 rounded-lg bg-[var(--rsi-purple-tint)] grid place-items-center text-[var(--rsi-purple)]">
                <Percent className="h-5 w-5" />
              </div>
              <div>
                <h2 className="font-display font-extrabold text-xl tracking-tight text-[var(--rsi-navy)]">
                  Tax calculation engine
                </h2>
                <p className="text-sm text-gray-500">Use product-linked defaults or enter manual rates for a scenario.</p>
              </div>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                calcMut.mutate(calcForm);
              }}
              className="grid gap-4"
            >
              <div>
                <Label>Product</Label>
                <select
                  value={calcForm.product_id}
                  onChange={(e) => setCalcForm((prev) => ({ ...prev, product_id: e.target.value }))}
                  className="mt-1.5 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm"
                >
                  <option value="">Manual scenario</option>
                  {products.map((product) => (
                    <option key={product.id} value={product.id}>
                      {product.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Product Name</Label>
                  <Input value={calcForm.product_name} onChange={(e) => setCalcForm((prev) => ({ ...prev, product_name: e.target.value }))} className="mt-1.5" />
                </div>
                <div>
                  <Label>HS Code</Label>
                  <Input value={calcForm.hs_code} onChange={(e) => setCalcForm((prev) => ({ ...prev, hs_code: e.target.value }))} className="mt-1.5" />
                </div>
                <div>
                  <Label>Category Slug</Label>
                  <Input value={calcForm.category_slug} onChange={(e) => setCalcForm((prev) => ({ ...prev, category_slug: e.target.value }))} className="mt-1.5" />
                </div>
                <div>
                  <Label>Origin Country</Label>
                  <Input value={calcForm.origin_country} onChange={(e) => setCalcForm((prev) => ({ ...prev, origin_country: e.target.value }))} className="mt-1.5" />
                </div>
              </div>

              <div className="grid md:grid-cols-4 gap-4">
                <div>
                  <Label>Invoice Value</Label>
                  <Input type="number" step="0.01" min="0" value={calcForm.invoice_value} onChange={(e) => setCalcForm((prev) => ({ ...prev, invoice_value: e.target.value }))} className="mt-1.5" />
                </div>
                <div>
                  <Label>Freight</Label>
                  <Input type="number" step="0.01" min="0" value={calcForm.freight_cost} onChange={(e) => setCalcForm((prev) => ({ ...prev, freight_cost: e.target.value }))} className="mt-1.5" />
                </div>
                <div>
                  <Label>Insurance</Label>
                  <Input type="number" step="0.01" min="0" value={calcForm.insurance_cost} onChange={(e) => setCalcForm((prev) => ({ ...prev, insurance_cost: e.target.value }))} className="mt-1.5" />
                </div>
                <div>
                  <Label>Other Cost</Label>
                  <Input type="number" step="0.01" min="0" value={calcForm.other_cost} onChange={(e) => setCalcForm((prev) => ({ ...prev, other_cost: e.target.value }))} className="mt-1.5" />
                </div>
              </div>

              <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-700">
                <input
                  type="checkbox"
                  checked={calcForm.use_profile_defaults}
                  onChange={(e) => setCalcForm((prev) => ({ ...prev, use_profile_defaults: e.target.checked }))}
                />
                Use matching tax profile defaults automatically
              </label>

              <div className="grid md:grid-cols-4 gap-4">
                <div><Label>Duty %</Label>{rateInput(calcForm.duty_rate, (value) => setCalcForm((prev) => ({ ...prev, duty_rate: value })))}</div>
                <div><Label>Reg. Duty %</Label>{rateInput(calcForm.regulatory_duty_rate, (value) => setCalcForm((prev) => ({ ...prev, regulatory_duty_rate: value })))}</div>
                <div><Label>Supp. Duty %</Label>{rateInput(calcForm.supplementary_duty_rate, (value) => setCalcForm((prev) => ({ ...prev, supplementary_duty_rate: value })))}</div>
                <div><Label>VAT %</Label>{rateInput(calcForm.vat_rate, (value) => setCalcForm((prev) => ({ ...prev, vat_rate: value })))}</div>
                <div><Label>Advance Tax %</Label>{rateInput(calcForm.advance_tax_rate, (value) => setCalcForm((prev) => ({ ...prev, advance_tax_rate: value })))}</div>
                <div><Label>Adv. Income Tax %</Label>{rateInput(calcForm.advance_income_tax_rate, (value) => setCalcForm((prev) => ({ ...prev, advance_income_tax_rate: value })))}</div>
                <div><Label>Exemption %</Label>{rateInput(calcForm.exemption_rate, (value) => setCalcForm((prev) => ({ ...prev, exemption_rate: value })))}</div>
                <div><Label>Rebate %</Label>{rateInput(calcForm.rebate_rate, (value) => setCalcForm((prev) => ({ ...prev, rebate_rate: value })))}</div>
              </div>

              <div className="flex justify-end">
                <button type="submit" className="btn-primary" disabled={calcMut.isPending}>
                  {calcMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Calculator className="h-4 w-4" />}
                  Calculate tax
                </button>
              </div>
            </form>
          </section>

          <section className="rsi-card p-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="h-10 w-10 rounded-lg bg-[var(--rsi-purple-tint)] grid place-items-center text-[var(--rsi-purple)]">
                <FileBadge2 className="h-5 w-5" />
              </div>
              <div>
                <h2 className="font-display font-extrabold text-xl tracking-tight text-[var(--rsi-navy)]">
                  Tax profiles
                </h2>
                <p className="text-sm text-gray-500">Create reusable rate profiles by product, category, HS prefix, or as a global fallback.</p>
              </div>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                saveProfileMut.mutate(profileForm);
              }}
              className="grid gap-4"
            >
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Profile Name</Label>
                  <Input value={profileForm.name} onChange={(e) => setProfileForm((prev) => ({ ...prev, name: e.target.value }))} className="mt-1.5" />
                </div>
                <div>
                  <Label>Scope</Label>
                  <select
                    value={profileForm.scope}
                    onChange={(e) => setProfileForm((prev) => ({ ...prev, scope: e.target.value }))}
                    className="mt-1.5 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm"
                  >
                    <option value="global">Global</option>
                    <option value="category">Category</option>
                    <option value="product">Product</option>
                    <option value="hs_prefix">HS Prefix</option>
                  </select>
                </div>
                <div>
                  <Label>Product ID</Label>
                  <Input value={profileForm.product_id} onChange={(e) => setProfileForm((prev) => ({ ...prev, product_id: e.target.value }))} className="mt-1.5" />
                </div>
                <div>
                  <Label>Category Slug</Label>
                  <Input value={profileForm.category_slug} onChange={(e) => setProfileForm((prev) => ({ ...prev, category_slug: e.target.value }))} className="mt-1.5" />
                </div>
                <div>
                  <Label>HS Prefix</Label>
                  <Input value={profileForm.hs_prefix} onChange={(e) => setProfileForm((prev) => ({ ...prev, hs_prefix: e.target.value }))} className="mt-1.5" />
                </div>
                <div>
                  <Label>Origin Country</Label>
                  <Input value={profileForm.origin_country} onChange={(e) => setProfileForm((prev) => ({ ...prev, origin_country: e.target.value }))} className="mt-1.5" />
                </div>
              </div>

              <div className="grid md:grid-cols-4 gap-4">
                <div><Label>Duty %</Label>{rateInput(profileForm.duty_rate, (value) => setProfileForm((prev) => ({ ...prev, duty_rate: value })))}</div>
                <div><Label>Reg. Duty %</Label>{rateInput(profileForm.regulatory_duty_rate, (value) => setProfileForm((prev) => ({ ...prev, regulatory_duty_rate: value })))}</div>
                <div><Label>Supp. Duty %</Label>{rateInput(profileForm.supplementary_duty_rate, (value) => setProfileForm((prev) => ({ ...prev, supplementary_duty_rate: value })))}</div>
                <div><Label>VAT %</Label>{rateInput(profileForm.vat_rate, (value) => setProfileForm((prev) => ({ ...prev, vat_rate: value })))}</div>
                <div><Label>Advance Tax %</Label>{rateInput(profileForm.advance_tax_rate, (value) => setProfileForm((prev) => ({ ...prev, advance_tax_rate: value })))}</div>
                <div><Label>Adv. Income Tax %</Label>{rateInput(profileForm.advance_income_tax_rate, (value) => setProfileForm((prev) => ({ ...prev, advance_income_tax_rate: value })))}</div>
                <div><Label>Exemption %</Label>{rateInput(profileForm.exemption_rate, (value) => setProfileForm((prev) => ({ ...prev, exemption_rate: value })))}</div>
                <div><Label>Rebate %</Label>{rateInput(profileForm.rebate_rate, (value) => setProfileForm((prev) => ({ ...prev, rebate_rate: value })))}</div>
              </div>

              <div>
                <Label>Profile Notes</Label>
                <Textarea rows={3} value={profileForm.notes} onChange={(e) => setProfileForm((prev) => ({ ...prev, notes: e.target.value }))} className="mt-1.5" />
              </div>
              <div>
                <Label>Lawful Tax-Saving Tips</Label>
                <Textarea rows={4} value={profileForm.legal_tips} onChange={(e) => setProfileForm((prev) => ({ ...prev, legal_tips: e.target.value }))} className="mt-1.5 font-mono-plex" />
                <p className="mt-1 text-xs text-gray-500">Use one lawful tip per line. These appear in the calculation result as legal guidance only.</p>
              </div>

              <div className="flex gap-3 justify-end">
                {editingProfileId ? (
                  <button type="button" className="btn-ghost" onClick={() => { setEditingProfileId(""); setProfileForm(EMPTY_PROFILE); }}>
                    Cancel edit
                  </button>
                ) : null}
                <button type="submit" className="btn-primary" disabled={saveProfileMut.isPending}>
                  {saveProfileMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  {editingProfileId ? "Update profile" : "Save profile"}
                </button>
              </div>
            </form>

            <div className="mt-6 space-y-3">
              {isLoading ? (
                <div className="text-sm text-gray-500">Loading tax profiles…</div>
              ) : profiles.length ? (
                profiles.map((profile) => (
                  <div key={profile.id} className="rounded-2xl border border-gray-100 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="font-semibold text-[var(--rsi-navy)]">{profile.name}</div>
                        <div className="text-xs text-gray-500 mt-1">
                          {profile.scope} {profile.hs_prefix ? `· HS ${profile.hs_prefix}` : ""} {profile.category_slug ? `· ${profile.category_slug}` : ""}
                        </div>
                        <div className="text-xs text-gray-500 mt-2">
                          Duty {profile.duty_rate}% · VAT {profile.vat_rate}% · Exemption {profile.exemption_rate}% · Rebate {profile.rebate_rate}%
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button type="button" className="btn-ghost" onClick={() => startEdit(profile)}>Edit</button>
                        <button type="button" className="btn-ghost !text-red-600" onClick={() => deleteProfileMut.mutate(profile.id)}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-sm text-gray-500">No tax profiles yet.</div>
              )}
            </div>
          </section>
        </div>

        <div className="xl:col-span-5">
          <section className="rsi-card p-6 sticky top-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="h-10 w-10 rounded-lg bg-[var(--rsi-purple-tint)] grid place-items-center text-[var(--rsi-purple)]">
                <ShieldCheck className="h-5 w-5" />
              </div>
              <div>
                <h2 className="font-display font-extrabold text-xl tracking-tight text-[var(--rsi-navy)]">
                  Calculation result
                </h2>
                <p className="text-sm text-gray-500">Admin-only working paper for lawful duty planning and cost review.</p>
              </div>
            </div>

            {result ? (
              <div className="space-y-5">
                <div className="grid grid-cols-2 gap-4">
                  <div className="rounded-2xl bg-[var(--rsi-bg-subtle)] p-4">
                    <div className="text-xs uppercase tracking-[0.18em] text-gray-400">Net Tax</div>
                    <div className="mt-2 font-display text-3xl font-black tracking-tight text-[var(--rsi-navy)]">
                      {result.currency} {result.net_tax}
                    </div>
                  </div>
                  <div className="rounded-2xl bg-[var(--rsi-bg-subtle)] p-4">
                    <div className="text-xs uppercase tracking-[0.18em] text-gray-400">Landed Cost</div>
                    <div className="mt-2 font-display text-3xl font-black tracking-tight text-[var(--rsi-navy)]">
                      {result.currency} {result.landed_cost}
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-gray-100 p-4 text-sm text-gray-700 space-y-1">
                  <div><span className="font-semibold text-[var(--rsi-navy)]">Matched Profile:</span> {matchedProfile?.name || result.matched_profile_name || "Manual/default rates"}</div>
                  <div><span className="font-semibold text-[var(--rsi-navy)]">HS Code:</span> {result.hs_code || "—"}</div>
                  <div><span className="font-semibold text-[var(--rsi-navy)]">Effective Tax Rate:</span> {result.effective_tax_rate}%</div>
                  <div><span className="font-semibold text-[var(--rsi-navy)]">Assessable Value:</span> {result.currency} {result.assessable_value}</div>
                </div>

                <div className="space-y-2">
                  {result.lines.map((line) => (
                    <div key={line.label} className="rounded-2xl border border-gray-100 p-4">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <div className="font-semibold text-[var(--rsi-navy)]">{line.label}</div>
                          <div className="text-xs text-gray-500">Rate {line.rate}% on {result.currency} {line.base_amount}</div>
                        </div>
                        <div className="font-display font-black text-xl text-[var(--rsi-navy)]">
                          {result.currency} {line.tax_amount}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="rounded-2xl bg-[var(--rsi-purple)]/5 border border-[var(--rsi-purple)]/10 p-4">
                  <div className="font-semibold text-[var(--rsi-navy)]">Lawful savings & exemption review</div>
                  <ul className="mt-3 space-y-2 text-sm text-gray-700">
                    {(result.legal_savings_tips || []).map((tip) => <li key={tip}>- {tip}</li>)}
                  </ul>
                </div>

                {result.warnings?.length ? (
                  <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4">
                    <div className="font-semibold text-amber-900">Important compliance notes</div>
                    <ul className="mt-3 space-y-2 text-sm text-amber-800">
                      {result.warnings.map((warning) => <li key={warning}>- {warning}</li>)}
                    </ul>
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="text-sm text-gray-500">Run a calculation to see tax, landed cost, profile matching, and lawful savings guidance.</div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
